import { ChevronRight, Sparkles } from 'lucide-react'
import * as React from 'react'

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { Separator } from '@/shared/components/ui/separator'
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuItem,
    SidebarSeparator,
} from '@/shared/components/ui/sidebar'
import CharacterLocationTabbar from './CharacterLocationTabbar'
//import json_script from './json_scripts/dark_knight.json'
import { ToggleCard } from '@/components/custom/ToggelCard'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { useSpEditorProvider } from './editor/SpEditorProvider'
import { SceneCard } from '@/features/shared/components/SceneCard'
import { Button } from '@/shared/components/ui/button'
import { useSpEditorStore } from '../stores/sp-editor.store'

export function SpOptionBar({ onGenerateClick, ...props }: React.ComponentProps<typeof Sidebar> & { onGenerateClick?: () => void }) {
    const { updateFeatureEnabled, featuresEnabled } = useSpEditorProvider()
    const setIsChoiceDialogOpen = useSpEditorStore((state) => state.setIsChoiceDialogOpen)

    const { scenes, editor } = useSceneStore()

    const handleGenerateClick = onGenerateClick || (() => setIsChoiceDialogOpen(true))

    return (
        <Sidebar
            collapsible="none"
            className="sticky top-0 hidden h-svh border-l lg:flex"
            {...props}
        >
            <SidebarContent>
                {/* <DatePicker /> */}
                <CharacterLocationTabbar />
                <SidebarSeparator className="mx-0" />
                <SidebarGroup>
                    <SidebarGroupLabel>AI Actions</SidebarGroupLabel>
                    <SidebarGroupContent className="p-2">
                        <Button
                            variant="outline"
                            className="w-full justify-start gap-2 border-dashed border-primary/50 hover:border-primary hover:bg-primary/5"
                            onClick={handleGenerateClick}
                        >
                            <Sparkles className="h-4 w-4 text-primary" />
                            <span>Generate Screenplay Outline</span>
                        </Button>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarSeparator className="mx-0" />
                <Collapsible
                    defaultOpen={true}
                    className="group/collapsible flex flex-col flex-grow min-h-0"
                >
                    <SidebarGroupLabel
                        asChild
                        className="group/label text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground w-full text-sm"
                    >
                        <CollapsibleTrigger>
                            Scene Map
                            <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                        </CollapsibleTrigger>
                    </SidebarGroupLabel>
                    <CollapsibleContent className="flex flex-col flex-grow min-h-0">
                        <ScrollArea className="flex-grow min-h-0 rounded-md border">
                            <div className="p-4 font-script flex flex-col gap-1.5">
                                {scenes.map((element, index) => {
                                    const text = editor?.read(() => getSceneHeader(element));
                                    return (
                                        <SceneCard
                                            key={element.id || index}
                                            index={index + 1}
                                            title={text}
                                            href={`#scene-${element.id}`}
                                        />
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </CollapsibleContent>
                </Collapsible>
            </SidebarContent>
            <SidebarFooter>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <ToggleCard
                            id="toggle-ai-suggestion"
                            titleEnabled="AI Suggestions On"
                            titleDisabled="AI Suggestions Off"
                            description="Suggests text as you type."
                            checked={featuresEnabled.aiCompletion}
                            onChange={async (checked) => {
                                const feature = {
                                    ...featuresEnabled,
                                    aiCompletion: checked,
                                }
                                updateFeatureEnabled(feature)
                            }}
                        />
                    </SidebarMenuItem>

                    <SidebarMenuItem>
                        <ToggleCard
                            id="toggle-node-toolbar"
                            titleEnabled="Node Toolbar Shown"
                            titleDisabled="Show Node Toolbar"
                            description="Floating tools at end of lines."
                            checked={featuresEnabled.nodeToolbar}
                            onChange={(checked) => {
                                updateFeatureEnabled({ ...featuresEnabled, nodeToolbar: checked })
                            }}
                        />
                    </SidebarMenuItem>

                    <SidebarMenuItem>
                        <ToggleCard
                            id="toggle-suggestion-dropdown"
                            titleEnabled="Dropdown Suggestions On"
                            titleDisabled="Show Suggestions Dropdown"
                            description="Shows quick command suggestions."
                            checked={featuresEnabled.suggestionDropdown}
                            onChange={(checked) => {
                                updateFeatureEnabled({
                                    ...featuresEnabled,
                                    suggestionDropdown: checked,
                                })
                            }}
                        />
                    </SidebarMenuItem>

                    {/* <SidebarMenuButton onClick={() => {}}>
                        <Plus />
                        <span>Sample Button</span>
                    </SidebarMenuButton> */}
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    )
}
